import React, { useCallback, useEffect, useState } from 'react';

import { EPanelOpenMethod } from '@components/block-event-single/store';
import { IMappingRulesManagement } from '@components/mapped-event-management/components/MappingRulesManagement/typed';
import RuleBuilder from '@components/mapping-panel/components/RuleBuilder';
import { EInputType } from '@components/mapping-panel/components/RuleBuilder/KeyValueBuilder/typed';
import {
  IActiveIndices,
  IItemActiveIndices,
  IMappingRules,
  IMetadataItemActiveIndices,
} from '@components/mapping-panel/store';
import { EMappingRuleType, ERuleActionType } from '@components/mapping-panel/typed';
import {
  dragRulesChange,
  rulesChange,
  transformRulesToState,
  transformStateToRules,
  updateRules,
} from '@components/mapping-panel/utils/handleRulesUtils';
import { getInitialActiveIndices } from '@components/mapping-panel/utils/initializeUtils';
import { MappedEvent } from '@src/emp-3.0/types/mapped-event';

const MappingRulesManagement: React.FC<IMappingRulesManagement> = ({ mode, data, onChange }) => {
  const isViewMode = mode === EPanelOpenMethod.VIEW;

  const [mappedEvent, setMappedEvent] = useState<MappedEvent | undefined>(data);
  const [mappingRules, setMappingRules] = useState<IMappingRules[]>(
    transformRulesToState(data?.rules as any),
  );
  const [activeIndices, setActiveIndices] = useState<IActiveIndices>(getInitialActiveIndices());

  useEffect(() => {
    // Reset activeIndices each time switch from edit mode to view mode.
    isViewMode && setActiveIndices(getInitialActiveIndices());
  }, [mode]);

  useEffect(() => {
    // Reset activeIndices each time toggle mapped event
    if (
      mappedEvent?.event_name !== data?.event_name ||
      mappedEvent?.version_id !== data?.version_id
    ) {
      setActiveIndices(getInitialActiveIndices());
    }
    setMappedEvent(data);
    // init mapping rules
    setMappingRules(transformRulesToState(data?.rules as any));
  }, [data]);

  const handleStateChange = useCallback(
    (value: IMappingRules[]) => {
      setMappingRules(value);
      if (data) {
        const rules = transformStateToRules(value);
        onChange?.({ ...data, rules: [rules as any] });
      }
    },
    [data, mappingRules, onChange],
  );

  const updateActiveIndices = useCallback(
    (
      orIndex?: number,
      eventName?: boolean,
      ruleItems?: IItemActiveIndices[],
      metadataName?: boolean,
      metadataItems?: IMetadataItemActiveIndices[],
    ) => {
      setActiveIndices({
        ruleType: EMappingRuleType.NORMAL,
        orIndex,
        eventName,
        metadataName,
        ruleItems,
        metadataItems,
      });
    },
    [],
  );

  const handleChange = (
    orIndex: number,
    subAndIndex: number,
    subOrIndex: number,
    type: EInputType,
    value: string,
  ) => {
    handleStateChange([
      ...updateRules(mappingRules, orIndex, subAndIndex, subOrIndex, type, value),
    ]);
    return;
  };

  const handleRulesChange = (
    actionType: ERuleActionType,
    orIndex = 0,
    subAndIndex = 0,
    subOrIndex?: number,
    metadataKey?: string,
  ) => {
    const updatedRules = rulesChange(
      mappingRules,
      actionType,
      orIndex,
      subAndIndex,
      subOrIndex,
      metadataKey,
    );
    handleStateChange(updatedRules);
  };

  const handleDragEnd = (result) => {
    try {
      const res = dragRulesChange(result, EMappingRuleType.NORMAL, mappingRules);
      if (res) {
        const { rules, activeIndices } = res;
        setMappingRules([...rules]);
        setActiveIndices(activeIndices);
      }
    } catch (e) {
      console.log('Drag drop end error: ', e);
    }
  };

  return (
    <RuleBuilder
      mode={mode}
      showTitle={false}
      data={mappingRules}
      activeIndices={activeIndices}
      onChange={(...rest) => handleChange(...rest)}
      updateActiveIndices={(...rest) => updateActiveIndices(...rest)}
      onRulesChange={(...rest) => handleRulesChange(...rest)}
      onDragEnd={(result) => handleDragEnd(result)}
      showAddNewEventRuleBtn={!isViewMode}
    />
  );
};

export default MappingRulesManagement;
